import { type ReactNode, useEffect } from 'react';
import { useNavigate } from '@repo/poly-hooks/routing';
import { useSegment } from '@repo/poly-hooks/segment';
import { DropdownLanguage } from '@repo/shared-kit/components/dropdown-language/dropdown-language';
import { SignupTopbar } from '@repo/shared-kit/registration/signup-topbar/signup-topbar';
import { DOUGS_PARTNER_FLOW_VARITAION } from '@repo/runtime-kit/constants/dougs';
import { TopBanner } from '@repo/design-system-kit/components/top-banner/top-banner';
import welcomeIllustration from '@repo/visual-assets/illustrations/welcome.png';
import welcomeInvoicingIllustration from '@repo/visual-assets/illustrations/welcome-invoicing.png';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router';
import cx from 'clsx';
import {
  DOUGS_PARTNER_DISPLAY_NAME,
  isDougsPartnerName,
} from '#common/hooks/use-is-cobranded-dougs';
import { getWelcomeContent } from './welcome-content';
import { CustomerLogosBar } from './welcome-landing/customer-logos-bar';
import { WelcomeLanding } from './welcome-landing/welcome-landing';
import type { PartnerRedirectionOutletContext } from './partner-redirection-route';
import styles from './welcome-route.module.css';

export function PartnerRedirectionWelcomeRoute(): ReactNode {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const segment = useSegment();
  const { token } = useOutletContext<PartnerRedirectionOutletContext>();
  const legalFlow = token.legalFlow ?? 'company_creation';
  const isInvoicing = legalFlow === 'qonto_invoicing';
  const isBeglaubigt = token.partnerName?.toLowerCase().includes('beglaubigt') ?? false;
  const isDougs = isDougsPartnerName(token.partnerName);
  const { ctaLabel, featuresList, subtitle, title } = getWelcomeContent({
    formatMessage,
    isAccountant: token.partnerType === 'accountant',
    isEmbedded: token.partnerType === 'embed',
    isExistingCompany: legalFlow === 'existing_company',
    isInvoicing,
    isLegalPlaceFastTrack:
      !token.companyCreationFromScratch && token.partnerName?.toLowerCase() === 'legalplace',
    organizationName: token.organizationName,
    ownerFirstName: token.ownerFirstName,
    partnerDisplayName: isDougs ? DOUGS_PARTNER_DISPLAY_NAME : undefined,
    partnerName: token.partnerName,
  });

  useEffect(() => {
    const isCompanyCreation = legalFlow === 'company_creation';
    segment.track('onboarding_partner_register_started', {
      country_selected: token.organizationLegalCountry ?? 'FR',
      situation: isCompanyCreation ? 'company_creation' : 'company',
      under_registration: isCompanyCreation,
      organization_id: token.organizationId ?? '',
      partner_name: token.partnerName ?? '',
      ...(isInvoicing && { flow: 'fr_embed_qf' }),
    });
    segment.track('onboarding_partner_register_eligibility_confirmed', {
      ...(isInvoicing && { flow: 'fr_embed_qf' }),
    });
  }, [isInvoicing, legalFlow, segment, token]);

  return (
    <div className={cx(styles.page, isBeglaubigt && styles.withPromoBanner)}>
      {isBeglaubigt ? (
        <TopBanner className={styles.promoBanner} dismissable={false} type="info">
          <FormattedMessage id="customer-registration-kit.partner-redirection.welcome.promo-banner.beglaubigt" />
        </TopBanner>
      ) : null}
      <div className={styles.landingContent}>
        <WelcomeLanding
          featuresList={featuresList}
          illustration={
            <img
              alt=""
              role="presentation"
              src={isInvoicing ? welcomeInvoicingIllustration : welcomeIllustration}
            />
          }
          logosBar={
            <CustomerLogosBar country={token.organizationLegalCountry === 'DE' ? 'DE' : 'FR'} />
          }
          primaryAction={{
            label: ctaLabel,
            onPress: () => navigate(`/${token.id}/email-confirmation`),
          }}
          socialProofLabel={formatMessage({
            id: 'customer-registration-kit.partner-redirection.welcome.socialproof',
          })}
          subtitle={subtitle}
          title={title}
          topbar={
            <div className={styles.topbar}>
              <SignupTopbar
                bordered={false}
                keepLogoOnMobile
                showSignIn={false}
                variant={isDougs ? DOUGS_PARTNER_FLOW_VARITAION : 'default'}
              />
            </div>
          }
        />
        <footer className={styles.mobileLanguageSwitcher}>
          <DropdownLanguage />
        </footer>
      </div>
    </div>
  );
}
